import { useState } from 'react';
import axios from 'axios';
import type { UserSession } from '../contexts/UserContext';

interface LocalLoginProps {
  onLoginSuccess: (accessToken: string, user: UserSession) => void;
  onError?: (message: string) => void;
}

type OtpStep = {
  challengeId: string;
  maskedEmail: string;
};

export default function LocalLogin({ onLoginSuccess, onError }: LocalLoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [otpStep, setOtpStep] = useState<OtpStep | null>(null);
  const [otpCode, setOtpCode] = useState('');

  const base = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/$/, '');

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post<{
        success: boolean;
        accessToken?: string;
        user?: UserSession;
        emailOtpRequired?: boolean;
        challengeId?: string;
        maskedEmail?: string;
      }>(`${base}/auth/local-login`, { email: email.trim(), password }, { headers: { 'Content-Type': 'application/json' } });

      if (data.success && data.emailOtpRequired && data.challengeId && data.maskedEmail) {
        setOtpStep({ challengeId: data.challengeId, maskedEmail: data.maskedEmail });
        setOtpCode('');
        return;
      }

      if (!data.success || !data.accessToken || !data.user) {
        throw new Error('Unexpected response from server');
      }

      onLoginSuccess(data.accessToken, data.user);
    } catch (err: unknown) {
      const message =
        axios.isAxiosError(err) && err.response?.data?.message
          ? String(err.response.data.message)
          : err instanceof Error
            ? err.message
            : 'Sign-in failed';
      onError?.(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpStep) return;
    const trimmed = otpCode.replace(/\D/g, '').slice(0, 6);
    if (trimmed.length !== 6) {
      onError?.('Enter the 6-digit code from your email.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post<{
        success: boolean;
        accessToken: string;
        user: UserSession;
      }>(
        `${base}/auth/local-login/verify`,
        { challengeId: otpStep.challengeId, code: trimmed },
        { headers: { 'Content-Type': 'application/json' } },
      );

      if (!data.success || !data.accessToken || !data.user) {
        throw new Error('Unexpected response from server');
      }

      onLoginSuccess(data.accessToken, data.user);
      setOtpStep(null);
      setOtpCode('');
    } catch (err: unknown) {
      const message =
        axios.isAxiosError(err) && err.response?.data?.message
          ? String(err.response.data.message)
          : axios.isAxiosError(err) && err.response?.data?.error
            ? String(err.response.data.error)
            : err instanceof Error
              ? err.message
              : 'Verification failed';
      onError?.(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (otpStep) {
    return (
      <form onSubmit={handleOtpSubmit} style={{ width: '100%', textAlign: 'left' }}>
        <p style={{ color: '#6c757d', marginBottom: '20px', lineHeight: 1.5 }}>
          We sent a 6-digit code to <strong>{otpStep.maskedEmail}</strong>. Enter it to finish signing in.
        </p>
        <label
          htmlFor="local-otp-code"
          style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: 'var(--secondary-color)' }}
        >
          Verification code
        </label>
        <input
          id="local-otp-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={otpCode}
          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          required
          style={{
            width: '100%',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid #ced4da',
            fontSize: '1.25rem',
            letterSpacing: '0.3em',
            textAlign: 'center',
            boxSizing: 'border-box',
            marginBottom: '20px',
          }}
        />
        <button
          type="submit"
          disabled={submitting}
          style={{
            width: '100%',
            padding: '16px 24px',
            fontSize: '1.1rem',
            fontWeight: 600,
            cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.6 : 1,
            border: '1px solid #FC5003',
            color: '#fff',
            background: submitting ? '#ccc' : '#FC5003',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <span className="material-symbols-outlined">{submitting ? 'hourglass_empty' : 'verified_user'}</span>
          {submitting ? 'Verifying…' : 'Verify and sign in'}
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={() => {
            setOtpStep(null);
            setOtpCode('');
            setPassword('');
          }}
          style={{
            width: '100%',
            marginTop: '16px',
            padding: '12px',
            background: 'none',
            border: 'none',
            color: '#6c757d',
            cursor: 'pointer',
            fontSize: '0.95rem',
          }}
        >
          Back to sign in
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handlePasswordSubmit} style={{ width: '100%', textAlign: 'left' }}>
      <div style={{ marginBottom: '16px' }}>
        <label
          htmlFor="local-email"
          style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: 'var(--secondary-color)' }}
        >
          Email
        </label>
        <input
          id="local-email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={{
            width: '100%',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid #ced4da',
            fontSize: '1rem',
            boxSizing: 'border-box',
          }}
        />
      </div>
      <div style={{ marginBottom: '24px' }}>
        <label
          htmlFor="local-password"
          style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: 'var(--secondary-color)' }}
        >
          Password
        </label>
        <input
          id="local-password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          style={{
            width: '100%',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid #ced4da',
            fontSize: '1rem',
            boxSizing: 'border-box',
          }}
        />
      </div>
      <button
        type="submit"
        disabled={submitting}
        style={{
          width: '100%',
          padding: '16px 24px',
          fontSize: '1.1rem',
          fontWeight: 600,
          cursor: submitting ? 'not-allowed' : 'pointer',
          opacity: submitting ? 0.6 : 1,
          border: '1px solid #FC5003',
          color: '#FC5003',
          background: 'transparent',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
      >
        <span className="material-symbols-outlined">{submitting ? 'hourglass_empty' : 'login'}</span>
        {submitting ? 'Signing In...' : 'Sign In'}
      </button>
    </form>
  );
}
